import { useState, useEffect, useRef } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import { Loader2, Terminal as TerminalIcon, AlertCircle, ArrowLeft } from 'lucide-react';
import axios from 'axios';
import baseapi from '../../services/baseapi';
import { Terminal } from 'xterm';
import { FitAddon } from 'xterm-addon-fit';
import { WebLinksAddon } from 'xterm-addon-web-links';
import 'xterm/css/xterm.css';

interface AgentSSHProps {
    agentId?: string | number;
    clientId?: string | number;
    ip?: string;
    hideHeader?: boolean;
}

export default function AgentSSH({ agentId: propAgentId, clientId: propClientId, ip: propIp, hideHeader = false }: AgentSSHProps = {}) {
    const params = useParams<{ agentId: string }>();
    const location = useLocation();
    const navigate = useNavigate();

    // Route state parameters fallback
    const agentId = propAgentId ? String(propAgentId) : (params.agentId || '');
    const stateIp = propIp || location.state?.ip || '';
    const stateClientId = propClientId || location.state?.clientId || null;

    // States
    const [isDark, setIsDark] = useState<boolean>(() =>
        typeof document !== 'undefined' ? document.documentElement.classList.contains('dark') : true
    );

    useEffect(() => {
        const observer = new MutationObserver(() => {
            setIsDark(document.documentElement.classList.contains('dark'));
        });
        observer.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });
        return () => observer.disconnect();
    }, []);

    const [status, setStatus] = useState<'connecting' | 'connected' | 'ssh_connected' | 'error'>('connecting');
    const [errorMsg, setErrorMsg] = useState<string | null>(null);

    // CLI State
    const cliStateRef = useRef<'idle' | 'password' | 'ssh'>('idle');
    const commandBufferRef = useRef('');
    const sshTargetRef = useRef<{ ip: string; port: number; username: string } | null>(null);

    // Refs
    const wsRef = useRef<WebSocket | null>(null);
    const sessionIdRef = useRef<string | null>(null);
    const terminalRef = useRef<HTMLDivElement>(null);
    const xtermRef = useRef<Terminal | null>(null);
    const fitAddonRef = useRef<FitAddon | null>(null);

    // Dynamic xterm theme update on theme change
    useEffect(() => {
        if (xtermRef.current) {
            xtermRef.current.options.theme = isDark ? {
                background: '#000000',
                foreground: '#ffffff',
                cursor: '#00d4ff',
                cursorAccent: '#000000',
                selectionBackground: 'rgba(0, 212, 255, 0.3)',
                black: '#000000',
                red: '#ff4757',
                green: '#2ed573',
                yellow: '#ffa502',
                blue: '#00d4ff',
                magenta: '#ff6b9d',
                cyan: '#1e90ff',
                white: '#ffffff'
            } : {
                background: '#ffffff',
                foreground: '#0f172a',
                cursor: '#0284c7',
                cursorAccent: '#ffffff',
                selectionBackground: 'rgba(2, 132, 199, 0.25)',
                black: '#0f172a',
                red: '#dc2626',
                green: '#16a34a',
                yellow: '#d97706',
                blue: '#0284c7',
                magenta: '#db2777',
                cyan: '#0891b2',
                white: '#f8fafc'
            };
        }
    }, [isDark]);

    // Log helper (for console only now, terminal shows its own logs)
    const logEvent = (msg: string, type: 'info' | 'success' | 'error' | 'output' = 'info') => {
        console.log(`[SSH Log] ${type.toUpperCase()}: ${msg}`);
    };

    // Initialize Terminal
    useEffect(() => {
        if (!terminalRef.current || xtermRef.current) return;

        const terminal = new Terminal({
            cursorBlink: true,
            fontSize: 14,
            fontFamily: 'Menlo, Monaco, "Courier New", monospace',
            theme: isDark ? {
                background: '#000000',
                foreground: '#ffffff',
                cursor: '#00d4ff',
                cursorAccent: '#000000',
                selectionBackground: 'rgba(0, 212, 255, 0.3)',
                black: '#000000',
                red: '#ff4757',
                green: '#2ed573',
                yellow: '#ffa502',
                blue: '#00d4ff',
                magenta: '#ff6b9d',
                cyan: '#1e90ff',
                white: '#ffffff'
            } : {
                background: '#ffffff',
                foreground: '#0f172a',
                cursor: '#0284c7',
                cursorAccent: '#ffffff',
                selectionBackground: 'rgba(2, 132, 199, 0.25)',
                black: '#0f172a',
                red: '#dc2626',
                green: '#16a34a',
                yellow: '#d97706',
                blue: '#0284c7',
                magenta: '#db2777',
                cyan: '#0891b2',
                white: '#f8fafc'
            }
        });

        const fitAddon = new FitAddon();
        terminal.loadAddon(fitAddon);
        terminal.loadAddon(new WebLinksAddon());

        terminal.open(terminalRef.current);
        if (terminalRef.current.clientWidth > 0 && terminalRef.current.clientHeight > 0) {
            try { fitAddon.fit(); } catch (e) { }
        }

        xtermRef.current = terminal;
        fitAddonRef.current = fitAddon;

        terminal.writeln('\x1b[36m╔════════════════════════════════════════╗\x1b[0m');
        terminal.writeln('\x1b[36m║           Agent SSH Console            ║\x1b[0m');
        terminal.writeln('\x1b[36m╚════════════════════════════════════════╝\x1b[0m');
        terminal.writeln('');
        terminal.writeln('\x1b[33mConnecting to gateway session...\x1b[0m');

        // Handle terminal input
        terminal.onData(data => {
            const state = cliStateRef.current;

            if (state === 'ssh') {
                if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
                    wsRef.current.send(JSON.stringify({
                        type: 'input',
                        data: data
                    }));
                }
                return;
            }

            // Local CLI mode
            // Process each character in the input data
            for (let i = 0; i < data.length; i++) {
                const char = data[i];
                const code = char.charCodeAt(0);

                // Handle Backspace
                if (code === 127 || code === 8) {
                    if (commandBufferRef.current.length > 0) {
                        commandBufferRef.current = commandBufferRef.current.slice(0, -1);
                        if (cliStateRef.current === 'idle') {
                            terminal.write('\b \b');
                        }
                    }
                    continue;
                }

                // Handle Enter
                if (code === 13) {
                    terminal.write('\r\n');
                    const cmd = commandBufferRef.current.trim();
                    commandBufferRef.current = '';

                    const currentState = cliStateRef.current;
                    if (currentState === 'idle') {
                        if (cmd === '') {
                            terminal.write('\x1b[32m[agent-session]$\x1b[0m ');
                            continue;
                        }

                        const sshMatch = cmd.match(/^ssh\s+([^@\s]+)@([^\s]+)(?:\s+-p\s+(\d+))?/);
                        if (sshMatch) {
                            const [, username, ip, portStr] = sshMatch;
                            sshTargetRef.current = {
                                username,
                                ip,
                                port: portStr ? parseInt(portStr, 10) : 22
                            };
                            cliStateRef.current = 'password';
                            terminal.write(`${username}@${ip}'s password: `);
                        } else if (cmd === 'exit') {
                            if (wsRef.current) wsRef.current.close();
                        } else {
                            terminal.writeln(`bash: ${cmd.split(' ')[0]}: command not found`);
                            terminal.writeln('Available commands: ssh, exit');
                            terminal.writeln('Usage: ssh username@ip_address [-p port]');
                            terminal.write('\x1b[32m[agent-session]$\x1b[0m ');
                        }
                    } else if (currentState === 'password') {
                        const password = cmd;
                        const target = sshTargetRef.current;
                        if (target && wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
                            wsRef.current.send(JSON.stringify({
                                type: 'connect',
                                ip: target.ip,
                                port: target.port,
                                username: target.username,
                                password: password
                            }));
                            cliStateRef.current = 'ssh';
                        } else {
                            cliStateRef.current = 'idle';
                            terminal.write('\x1b[32m[agent-session]$\x1b[0m ');
                        }
                    }
                    continue;
                }

                // Printable characters
                if (code >= 32 && code <= 126) {
                    commandBufferRef.current += char;
                    if (cliStateRef.current === 'idle') {
                        terminal.write(char);
                    }
                }
            }
        });

        // Handle window resize
        const handleResize = () => {
            if (fitAddonRef.current && xtermRef.current && terminalRef.current) {
                if (terminalRef.current.clientWidth > 0 && terminalRef.current.clientHeight > 0) {
                    try {
                        fitAddonRef.current.fit();
                        if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
                            wsRef.current.send(JSON.stringify({
                                type: 'resize',
                                cols: xtermRef.current.cols,
                                rows: xtermRef.current.rows
                            }));
                        }
                    } catch (e) {
                        console.error('Resize error', e);
                    }
                }
            }
        };

        window.addEventListener('resize', handleResize);

        // Optional: ResizeObserver to fit addon when div size changes
        const resizeObserver = new ResizeObserver(() => {
            handleResize();
        });
        resizeObserver.observe(terminalRef.current);

        return () => {
            window.removeEventListener('resize', handleResize);
            if (terminalRef.current) {
                resizeObserver.unobserve(terminalRef.current);
            }
            resizeObserver.disconnect();
            terminal.dispose();
            xtermRef.current = null;
        };
    }, []); // Empty dependency array prevents recreation of the terminal

    // Start Session API call & WS setup
    useEffect(() => {
        let isMounted = true;

        const startSession = async () => {
            logEvent(`Starting shell session for agent: ${agentId}`);
            try {
                // If client_id is not passed in state, fetch the agent list to get it
                let clientId = stateClientId;

                if (!clientId) {
                    const response = await baseapi.get('/agents');
                    const agent = response.data?.data?.find((a: any) => String(a.id) === String(agentId));
                    if (agent) {
                        clientId = agent.client_id;
                    }
                }

                // Call start API directly (without /api)
                const startResponse = await axios.post(`${window.location.origin}/v3/shell/start`, {
                    agent_id: parseInt(agentId || '0', 10),
                    ...(clientId && { client_id: parseInt(String(clientId), 10) })
                }, {
                    headers: {
                        'Authorization': `Bearer ${localStorage.getItem('access_token') || ''}`,
                        'Content-Type': 'application/json'
                    }
                });

                if (!startResponse.data.status) {
                    throw new Error(startResponse.data.message || 'Failed to start session');
                }

                const { session_id, ws_url } = startResponse.data.data;
                sessionIdRef.current = session_id;
                logEvent(`Session created: ${session_id}`, 'success');

                // Connect WebSocket
                const token = localStorage.getItem('access_token') || '';
                const wsEndpoint = ws_url.includes('?') ? `${ws_url}&token=${token}` : `${ws_url}?token=${token}`;

                logEvent(`Connecting to WebSocket...`);
                const ws = new WebSocket(wsEndpoint);
                wsRef.current = ws;

                ws.onopen = () => {
                    logEvent('WebSocket connection established', 'success');
                    if (isMounted) {
                        setStatus('connected');
                    }

                    if (xtermRef.current) {
                        xtermRef.current.clear();
                        xtermRef.current.writeln('\x1b[33mType "ssh username@ip -p port" to connect to a target device.\x1b[0m');
                        xtermRef.current.writeln('');
                        xtermRef.current.write('\x1b[32m[agent-session]$\x1b[0m ');
                        cliStateRef.current = 'idle';
                        commandBufferRef.current = '';

                        // Send initial resize
                        ws.send(JSON.stringify({
                            type: 'resize',
                            cols: xtermRef.current.cols,
                            rows: xtermRef.current.rows
                        }));
                    }
                };

                ws.onmessage = (event) => {
                    try {
                        const msg = JSON.parse(event.data);
                        switch (msg.type) {
                            case 'output':
                                if (xtermRef.current) {
                                    xtermRef.current.write(msg.data);
                                }
                                break;
                            case 'agent_ready':
                                logEvent('Agent connected and ready', 'success');
                                break;
                            case 'waiting_for_agent':
                                logEvent(`Waiting for agent to connect... (state: ${msg.state})`, 'info');
                                break;
                            case 'connected':
                                logEvent('SSH connection successful', 'success');
                                if (isMounted) {
                                    setStatus('ssh_connected');
                                }
                                if (xtermRef.current) {
                                    xtermRef.current.clear();
                                    xtermRef.current.writeln('\x1b[32m✓ SSH Connected\x1b[0m\r\n');
                                }
                                break;
                            case 'error':
                                logEvent(`Error: ${msg.message}`, 'error');
                                if (xtermRef.current) {
                                    xtermRef.current.writeln(`\r\n\x1b[31mError: ${msg.message}\x1b[0m\r\n`);
                                    if (cliStateRef.current !== 'ssh') {
                                        cliStateRef.current = 'idle';
                                        commandBufferRef.current = '';
                                        sshTargetRef.current = null;
                                        xtermRef.current.write('\x1b[32m[agent-session]$\x1b[0m ');
                                    }
                                }
                                if (isMounted) {
                                    setStatus('connected');
                                }
                                break;
                            case 'disconnected':
                                logEvent('SSH session disconnected', 'info');
                                if (xtermRef.current) {
                                    xtermRef.current.writeln('\r\n\x1b[33mSSH connection closed\x1b[0m\r\n');
                                    cliStateRef.current = 'idle';
                                    commandBufferRef.current = '';
                                    sshTargetRef.current = null;
                                    xtermRef.current.write('\x1b[32m[agent-session]$\x1b[0m ');
                                }
                                if (isMounted) {
                                    setStatus('connected');
                                }
                                break;
                            case 'agent_disconnected':
                                logEvent(`Agent disconnected: ${msg.message}`, 'error');
                                if (xtermRef.current) {
                                    xtermRef.current.writeln(`\r\n\x1b[31mAgent disconnected: ${msg.message}\x1b[0m\r\n`);
                                }
                                if (isMounted) {
                                    setStatus('connected');
                                }
                                break;
                            default:
                                logEvent(`Message: ${JSON.stringify(msg)}`, 'info');
                        }
                    } catch (e) {
                        // Raw output
                        if (xtermRef.current) {
                            xtermRef.current.write(event.data);
                        }
                    }
                };

                ws.onerror = (err) => {
                    console.error('WebSocket error:', err);
                    logEvent('WebSocket error occurred', 'error');
                    if (isMounted) {
                        setStatus('error');
                        setErrorMsg('WebSocket connection error');
                    }
                };

                ws.onclose = (event) => {
                    logEvent(`WebSocket connection closed: ${event.code} - ${event.reason || 'No reason'}`);
                    if (isMounted) {
                        setStatus('error');
                        setErrorMsg('Connection closed');
                    }
                };

            } catch (err: any) {
                console.error(err);
                let errMsg = err.response?.data?.message || err.message || 'Failed to initialize session';

                if (err.response?.status === 409) {
                    errMsg = 'Session is already in use.';
                }

                logEvent(errMsg, 'error');
                if (isMounted) {
                    setStatus('error');
                    setErrorMsg(errMsg);
                }
            }
        };

        startSession();

        return () => {
            isMounted = false;
            if (wsRef.current) {
                wsRef.current.close();
            }
        };
    }, [agentId, stateIp, stateClientId]);



    const handleDisconnect = async () => {
        logEvent('Disconnecting session...', 'info');
        if (wsRef.current) {
            wsRef.current.close();
            wsRef.current = null;
        }
        setStatus('error');
        setErrorMsg('Session disconnected by user.');
    };

    return (
        <div className={`bg-white dark:bg-gray-950 text-slate-800 dark:text-white rounded-xl flex flex-col border border-slate-200 dark:border-gray-800 shadow-sm ${hideHeader ? 'p-4 min-h-[500px] h-full' : 'p-6 min-h-[calc(100vh-100px)]'}`}>
            {/* Top Navigation */}
            <div className="flex items-center justify-between mb-4 pb-3 border-b border-slate-200 dark:border-gray-800">
                <div className="flex items-center gap-3">
                    {!hideHeader && (
                        <button
                            onClick={() => navigate('/agents')}
                            className="p-2 bg-slate-100 hover:bg-slate-200 dark:bg-gray-900 dark:hover:bg-gray-800 border border-slate-200 dark:border-gray-800 rounded-lg transition-colors text-slate-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white cursor-pointer"
                        >
                            <ArrowLeft size={16} />
                        </button>
                    )}
                    <div>
                        <h1 className="text-base font-bold text-gray-900 dark:text-gray-200 flex items-center gap-2">
                            <TerminalIcon className="text-orange-500 w-5 h-5" />
                            Agent SSH Console
                        </h1>
                    </div>
                </div>

                <div className="flex items-center gap-2 bg-slate-100 dark:bg-gray-900 px-3 py-1.5 rounded-lg border border-slate-200 dark:border-gray-800">
                    <span className="text-[10px] uppercase font-bold text-slate-500 dark:text-gray-500 tracking-wider">Status:</span>
                    <div className="flex items-center gap-1.5">
                        <div className={`w-2 h-2 rounded-full ${status === 'ssh_connected' ? 'bg-green-500 animate-pulse' :
                            status === 'connected' ? 'bg-blue-500' :
                                status === 'error' ? 'bg-red-500' : 'bg-orange-500 animate-pulse'
                            }`} />
                        <span className="text-[10px] uppercase font-bold text-slate-700 dark:text-gray-300 font-mono">
                            {status.replace('_', ' ')}
                        </span>
                    </div>
                </div>
            </div>

            {/* Main Content Area */}
            <div className="flex-1 flex flex-col gap-4 min-h-0">
                {/* Loader state */}
                {(status === 'connecting') && (
                    <div className="flex-1 flex flex-col items-center justify-center bg-slate-50 dark:bg-gray-900 border border-slate-200 dark:border-gray-800 rounded-xl p-8 shadow-sm dark:shadow-2xl">
                        <Loader2 className="w-10 h-10 animate-spin text-orange-500 mb-4" />
                        <span className="text-xs font-bold text-slate-500 dark:text-gray-400 uppercase tracking-widest animate-pulse font-mono">
                            Starting Agent Shell Session...
                        </span>
                    </div>
                )}

                {/* Error/Disconnected state */}
                {status === 'error' && (
                    <div className={`flex-1 flex flex-col items-center justify-center bg-slate-50 dark:bg-gray-900 border rounded-xl p-8 shadow-sm dark:shadow-2xl text-center ${errorMsg?.includes('disconnected') || errorMsg?.includes('closed') ? 'border-orange-500/30' : 'border-red-500/30'}`}>
                        <AlertCircle className={`w-12 h-12 mb-4 ${errorMsg?.includes('disconnected') || errorMsg?.includes('closed') ? 'text-orange-500' : 'text-red-500'}`} />
                        <h2 className={`text-lg font-bold mb-2 ${errorMsg?.includes('disconnected') || errorMsg?.includes('closed') ? 'text-orange-600 dark:text-orange-400' : 'text-red-600 dark:text-red-400'}`}>
                            {errorMsg?.includes('disconnected') || errorMsg?.includes('closed') ? 'Session Closed' : 'Connection Failed'}
                        </h2>
                        <p className="text-sm text-slate-600 dark:text-gray-400 max-w-md mb-6 font-mono">{errorMsg || 'Unable to establish WebSocket shell session'}</p>
                        <button
                            onClick={() => window.location.reload()}
                            className="px-6 py-2 bg-orange-600 hover:bg-orange-500 text-white rounded-lg text-xs font-bold uppercase tracking-wider transition-colors shadow-md cursor-pointer"
                        >
                            {errorMsg?.includes('disconnected') || errorMsg?.includes('closed') ? 'Start New Session' : 'Retry'}
                        </button>
                    </div>
                )}

                {/* Terminal Simulator Container */}
                <div className={`flex-1 flex flex-col bg-white dark:bg-[#000000] border border-slate-200 dark:border-gray-800 rounded-xl overflow-hidden shadow-sm dark:shadow-2xl ${(status === 'connected' || status === 'ssh_connected') ? '' : 'hidden'}`}>
                    {/* Terminal Header */}
                    <div className="px-4 py-2 border-b border-slate-200 dark:border-gray-800 bg-slate-100 dark:bg-gray-900 flex items-center justify-between text-xs text-slate-600 dark:text-gray-400 font-mono shrink-0">
                        <div className="flex items-center gap-2">
                            <span className={`w-2 h-2 rounded-full ${status === 'ssh_connected' ? 'bg-green-500 animate-pulse' : 'bg-blue-500'}`} />
                            <span>{status === 'ssh_connected' && sshTargetRef.current ? `ssh: ${sshTargetRef.current.username}@${sshTargetRef.current.ip}:${sshTargetRef.current.port}` : 'bash'}</span>
                        </div>
                        {status === 'ssh_connected' && (
                            <button
                                type="button"
                                onClick={handleDisconnect}
                                className="text-red-600 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300 font-bold uppercase tracking-widest text-[10px] transition-colors cursor-pointer"
                            >
                                Disconnect SSH
                            </button>
                        )}
                    </div>

                    {/* Interactive Terminal Screen */}
                    <div
                        className="flex-1 relative w-full h-full p-2 bg-white dark:bg-black"
                    >
                        <div ref={terminalRef} className="absolute inset-0 p-2" />
                    </div>
                </div>
            </div>
        </div>
    );
}
